import { useMemo, useState } from "react"
import Select from "~/components/reusable/Select"
import { OTHER_GROUP_LABEL, RELEVANT_GROUP_LABEL } from "~/utils/groupedFilterOptions"

type EnumOption = { value: string; label: string }
type EnumOptionGroup = { label: string; options: EnumOption[] }

const PAGE_SIZE = 50

function toOption(value: string): EnumOption {
  return { value, label: value }
}

type CommonProps = {
  options: string[] | EnumOption[]
  // When provided, the dropdown renders two sections ("Relevant" / "Other options").
  // Falls back to a flat list if either group is empty.
  groupedOptions?: { relevant: string[]; other: string[] }
  size?: "sm" | "base"
  autoFocus?: boolean
  placeholder?: string
  isDisabled?: boolean
}

type SingleProps = CommonProps & {
  isMulti?: false
  value: string
  onValueChange: (value: string) => void
}

type MultiProps = CommonProps & {
  isMulti: true
  values: string[]
  onValuesChange: (values: string[]) => void
}

export function FilterAsyncEnumSelect(props: SingleProps | MultiProps) {
  const { options, groupedOptions, size = "base", autoFocus = false, isDisabled } = props
  const [inputValue, setInputValue] = useState("")
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const grouped = useMemo(() => {
    if (!groupedOptions) return null
    const relevant = groupedOptions.relevant.map(toOption)
    const other = groupedOptions.other.map(toOption)
    if (relevant.length === 0 || other.length === 0) return null
    return { relevant, other }
  }, [groupedOptions])

  const flatOptions = useMemo(() => {
    if (groupedOptions && (groupedOptions.relevant.length > 0 || groupedOptions.other.length > 0)) {
      const merged = [...groupedOptions.relevant, ...groupedOptions.other]
      return merged.map(toOption)
    }
    return options.map((o) => (typeof o === "string" ? toOption(o) : o))
  }, [options, groupedOptions])

  const q = inputValue.toLowerCase()
  const matches = (o: EnumOption) => o.label.toLowerCase().includes(q)

  const filteredFlat = useMemo(
    () => (inputValue ? flatOptions.filter(matches) : flatOptions),
    [flatOptions, inputValue, q],
  )

  const visibleFlat = useMemo(
    () => filteredFlat.slice(0, visibleCount),
    [filteredFlat, visibleCount],
  )

  // Grouped path: render all matched "Relevant" + a paginated slice of "Other options".
  // Falls back to flat (returns null) when search collapses one side — a single-group
  // dropdown with a header would be misleading.
  // `visibleCount` paginates only the "other" bucket; "relevant" is always shown in full.
  const groupedView = useMemo(() => {
    if (!grouped) return null
    const relevantMatches = inputValue ? grouped.relevant.filter(matches) : grouped.relevant
    const otherMatches = inputValue ? grouped.other.filter(matches) : grouped.other
    if (relevantMatches.length === 0 || otherMatches.length === 0) return null
    return {
      groups: [
        { label: RELEVANT_GROUP_LABEL, options: relevantMatches },
        { label: OTHER_GROUP_LABEL, options: otherMatches.slice(0, visibleCount) },
      ] as EnumOptionGroup[],
      otherTotal: otherMatches.length,
    }
  }, [grouped, inputValue, q, visibleCount])

  const handleInputChange = (
    value: string,
    meta: { action: "input-change" | "input-blur" | "menu-close" | "set-value" | string },
  ) => {
    if (meta.action === "input-change") {
      setInputValue(value)
      setVisibleCount(PAGE_SIZE)
    } else if (meta.action === "menu-close") {
      setInputValue("")
      setVisibleCount(PAGE_SIZE)
    } else if (meta.action === "set-value") {
      // Multi-select keeps the menu open, so collapsing back to the first page here would
      // yank a scrolled list out from under the user mid-selection.
      setInputValue("")
    }
  }

  const handleMenuScrollToBottom = () => {
    const total = groupedView ? groupedView.otherTotal : filteredFlat.length
    setVisibleCount((c) => (c >= total ? c : Math.min(c + PAGE_SIZE, total)))
  }

  const totalCount = flatOptions.length
  const effectiveDisabled = isDisabled ?? totalCount === 0
  const placeholder = props.placeholder ?? (totalCount ? "Select value" : "No values")

  const optionByValue = useMemo(() => new Map(flatOptions.map((o) => [o.value, o])), [flatOptions])

  if (props.isMulti) {
    return (
      <Select<EnumOption, true>
        isMulti
        options={groupedView?.groups ?? visibleFlat}
        value={props.values.map((v) => optionByValue.get(v) ?? toOption(v))}
        onChange={(next) => props.onValuesChange(next ? next.map((o) => o.value) : [])}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        filterOption={null}
        isDisabled={effectiveDisabled}
        placeholder={placeholder}
        size={size}
        autoFocus={autoFocus}
        openMenuOnFocus
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
      />
    )
  }

  return (
    <Select<EnumOption>
      options={groupedView?.groups ?? visibleFlat}
      value={props.value ? (optionByValue.get(props.value) ?? toOption(props.value)) : null}
      onChange={(next) => props.onValueChange(next?.value ?? "")}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onMenuScrollToBottom={handleMenuScrollToBottom}
      filterOption={null}
      isDisabled={effectiveDisabled}
      placeholder={placeholder}
      size={size}
      autoFocus={autoFocus}
      openMenuOnFocus
    />
  )
}
